import { Transition, TransitionChild } from '@headlessui/react';
import { useBreakpointMap } from '@kk/shared/hooks/useBreakpoint';
import Icon from '@kk/ui/components/Icon';
import { cn } from 'cn';
import { type Ref, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { UseCompanyInfoResponse } from '#/api/hooks/useCompanyInfo';
import type { UseExternalUserInfoResponse } from '#/api/hooks/useUserInfo';
import { logout } from '#/auth';
import MobileMenu from '#/components/Header/private/MobileMenu';
import { generateUrl } from '#/routes/generateUrl';
import { Logo } from '../Logo/Logo';
import { AccountMenu } from './private/AccountMenu';
import { Search } from './private/Search';

export type HeaderProps = {
  userInfo?: UseExternalUserInfoResponse;
  companyInfo?: UseCompanyInfoResponse;
};

const stateClasses = cn(
  'hover:kunde:bg-blue-100 active:bg-blue-60',
  'focus-visible:kunde:bg-blue-110 focus-visible:kunde:ring-blue-10 focus-visible:kunde:ring-2',
);

/**
 * @url https://kommunekredit.visualstudio.com/KK%20Laaneportal/_wiki/wikis/KK-Laaneportal.wiki/1943/Global-navigation
 * @url https://kommunekredit.visualstudio.com/KK%20Laaneportal/_wiki/wikis/KK-Laaneportal.wiki/2309/Databehov-Global-navigation-Side-navigation
 */
export function Header({ userInfo, companyInfo }: HeaderProps) {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const { minLg } = useBreakpointMap();
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const inRouter = !!userInfo?.emailVerified;

  function toggleSearch(): void {
    if (showSearch) {
      setShowSearch(false);
      searchButtonRef.current?.focus();
      return;
    }

    setShowSearch(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }

  return (
    <div className="relative">
      <header className="bg-blue-110 relative z-40 w-full px-6 text-white lg:px-2">
        <div className="flex h-16 lg:h-14 items-center justify-between">
          <div>
            <div id="error-portal" />
            <div className="flex items-center justify-between">
              <div className="flex h-full w-auto items-center">
                <Logo className={stateClasses} />
              </div>
              {companyInfo ? (
                <>
                  <div className="hidden h-6 mx-2 border-r border-white md:block" />
                  <Link
                    className={cn(
                      'typ-button-xs hidden md:block py-2 px-4 rounded-md',
                      stateClasses,
                    )}
                    to={generateUrl('/organisation')}
                  >
                    {companyInfo.companyName}
                  </Link>
                </>
              ) : null}
            </div>
          </div>
          {userInfo ? (
            <div className="flex items-center gap-0.5">
              <SearchControl
                inRouter={inRouter}
                minLg={minLg}
                showSearch={showSearch}
                searchLabel={t('search.placeholder')}
                buttonRef={searchButtonRef}
                onToggle={toggleSearch}
              />
              <AccountMenu
                user={userInfo}
                inRouter={inRouter}
                stateClasses={stateClasses}
              />
              <button
                onClick={() => setMobileMenuOpen(true)}
                aria-label={t('openMenu')}
                className={cn('lg:hidden p-2 rounded-md', stateClasses)}
              >
                <Icon type={'burger'} />
              </button>
            </div>
          ) : null}
        </div>
      </header>
      <MobileSearchPanel
        inRouter={inRouter}
        minLg={minLg}
        showSearch={showSearch}
        inputRef={searchInputRef}
        onClose={toggleSearch}
      />
      {minLg ? null : (
        <MobileMenu
          user={userInfo}
          menuOpen={mobileMenuOpen}
          companyName={userInfo ? companyInfo?.companyName : undefined}
          onClose={() => setMobileMenuOpen(false)}
          onLogOut={logout}
          inRouter={inRouter}
        />
      )}
    </div>
  );
}

interface SearchControlProps {
  inRouter: boolean;
  minLg: boolean;
  showSearch: boolean;
  searchLabel: string;
  buttonRef: Ref<HTMLButtonElement>;
  onToggle: () => void;
}

function SearchControl({
  inRouter,
  minLg,
  showSearch,
  searchLabel,
  buttonRef,
  onToggle,
}: SearchControlProps): React.ReactNode {
  if (minLg) return inRouter ? <Search className="mr-4" /> : null;

  return (
    <button
      aria-label={searchLabel}
      className={cn('text-white p-3 rounded-md', stateClasses, {
        'bg-blue-60 kunde:hover:bg-blue-60 pointer-events-none': showSearch,
      })}
      onClick={onToggle}
      ref={buttonRef}
    >
      <Icon type="magnify" size="small" />
    </button>
  );
}

interface MobileSearchPanelProps {
  inRouter: boolean;
  minLg: boolean;
  showSearch: boolean;
  inputRef: Ref<HTMLInputElement>;
  onClose: () => void;
}

function MobileSearchPanel({
  inRouter,
  minLg,
  showSearch,
  inputRef,
  onClose,
}: MobileSearchPanelProps): React.ReactNode {
  if (!inRouter) return null;

  return (
    <Transition
      as="div"
      className="bg-blue-110 absolute left-0 top-full z-10 w-full px-6 text-white lg:px-2 transition-transform duration-250 data-closed:-translate-y-16 data-leave:delay-100"
      show={showSearch && !minLg}
      unmount={!minLg}
    >
      <TransitionChild
        as="div"
        className="transition-opacity duration-200 data-closed:opacity-0 data-enter:delay-100"
        unmount={false}
      >
        <Search
          className={cn('mb-4 mt-2 transition-opacity duration-1000', {
            'opacity-0': !showSearch,
            'opacity-100': showSearch,
          })}
          ref={inputRef}
          onBlur={onClose}
        />
      </TransitionChild>
    </Transition>
  );
}
